import { NSARouterMakerResult } from "namirasoft-account-react";
import { ProductFullRow } from "namirasoft-api-product";
import { CategoryScopeRow, FieldRow, SlotEntityType, SlotInputRow, SlotPositionSide, SlotRow, SlotSize } from "namirasoft-field";
import { NSBaseMetaTable, NSBaseServer } from "namirasoft-site";
import { NSBox, NSBoxBoolean, NSBoxCombo, NSBoxEntity, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { FieldEntities } from "../FieldEntities";
import { FieldLayout } from "../FieldLayout";
import { FieldProps } from "../FieldProps";

interface SlotEditPageProps extends NSARouterMakerResult, FieldProps
{
	isEdit: boolean;
}

interface SlotEditPageState
{
	entities: { [name: string]: NSBaseMetaTable<NSBaseServer, {}>; };
	entity_type: SlotEntityType | null;
}

export default class SlotEditPage extends Component<SlotEditPageProps, SlotEditPageState>
{
	NSBoxEntity_Product = createRef<NSBoxEntity<ProductFullRow>>();
	NSBoxCombo_Entity = createRef<NSBoxCombo>();
	NSBoxString_Tab = createRef<NSBoxString>();
	NSBoxString_Name = createRef<NSBoxString>();
	NSBoxCombo_EntityType = createRef<NSBoxCombo>();
	NSBoxEntity_Field = createRef<NSBoxEntity<FieldRow>>();
	NSBoxEntity_CategoryScope = createRef<NSBoxEntity<CategoryScopeRow>>();
	NSBoxCombo_PositionSide = createRef<NSBoxCombo>();
	NSBoxString_PositionTarget = createRef<NSBoxString>();
	NSBoxCombo_Size = createRef<NSBoxCombo>();
	NSBoxBoolean_Required = createRef<NSBoxBoolean>();
	NSBoxBoolean_Multiple = createRef<NSBoxBoolean>();
	NSBoxTexArea_Description = createRef<NSBoxTextArea>();

	constructor(props: SlotEditPageProps)
	{
		super(props);
		this.state = { entities: {}, entity_type: null };
	}

	private getEntityID(): string
	{
		let entity_type = this.NSBoxCombo_EntityType.current?.getValueOne() as SlotEntityType | null;
		if (entity_type === SlotEntityType.Field)
			return this.NSBoxEntity_Field.current?.getValueOne() ?? "";
		if (entity_type === SlotEntityType.CategoryScope)
			return this.NSBoxEntity_CategoryScope.current?.getValueOne() ?? "";
		return "";
	}

	override render()
	{
		return (
			<FieldLayout<SlotRow, SlotInputRow>
				{...this.props}
				action={{
					description: this.props.isEdit ? "This section allows you to update the details of an existing slot." : "This section allows you to add a new slot by entering its details."
				}}
				pages={{
					entity: FieldEntities.slot(this.props),
					edit: {
						...this.props,
						ui: {
							tab_page_edit: {
								editor: {
									getInputRow: () =>
									{
										let product_id = this.NSBoxEntity_Product.current?.getValueOne() ?? "";
										let entity = this.NSBoxCombo_Entity.current?.getValueOne() ?? "";
										let tab = this.NSBoxString_Tab.current?.getValue() ?? "";
										let name = this.NSBoxString_Name.current?.getValue() ?? "";
										let entity_type = (this.NSBoxCombo_EntityType.current?.getValueOne() ?? SlotEntityType.Field) as SlotEntityType;
										let entity_id = this.getEntityID();
										let position_side = (this.NSBoxCombo_PositionSide.current?.getValueOne() ?? SlotPositionSide.After) as SlotPositionSide;
										let position_target = this.NSBoxString_PositionTarget.current?.getValue() ?? "";
										let size = (this.NSBoxCombo_Size.current?.getValueOne() ?? SlotSize.Medium) as SlotSize;
										let required = this.NSBoxBoolean_Required.current?.getValueBool() ?? false;
										let multiple = this.NSBoxBoolean_Multiple.current?.getValueBool() ?? false;
										let description = this.NSBoxTexArea_Description.current?.getValue() ?? "";

										return {
											workspace_id: "",
											product_id,
											entity,
											tab,
											name,
											entity_type,
											entity_id,
											position_side,
											position_target,
											size,
											required,
											multiple,
											description,
											slot_category: [],
											slot_field: [],
											slot_tag: [],
										};
									},
									setRow: (row) =>
									{
										this.NSBoxEntity_Product.current?.setValue(row.product_id, () =>
										{
											let entities: { [name: string]: NSBaseMetaTable<NSBaseServer, {}>; } = {};
											if (row.product_id)
												entities = FieldEntities.getFieldEntities(row.product_id);
											this.setState({ entities, entity_type: row.entity_type }, () =>
											{
												this.NSBoxCombo_Entity.current?.setValue(row.entity);
												this.NSBoxString_Tab.current?.setValue(row.tab);
												this.NSBoxString_Name.current?.setValue(row.name);
												this.NSBoxCombo_EntityType.current?.setValue(row.entity_type);
												if (row.entity_type === SlotEntityType.Field)
													this.NSBoxEntity_Field.current?.setValue(row.entity_id);
												else if (row.entity_type === SlotEntityType.CategoryScope)
													this.NSBoxEntity_CategoryScope.current?.setValue(row.entity_id);
												this.NSBoxCombo_PositionSide.current?.setValue(row.position_side);
												this.NSBoxString_PositionTarget.current?.setValue(row.position_target);
												this.NSBoxCombo_Size.current?.setValue(row.size);
												this.NSBoxBoolean_Required.current?.setValueBool(row.required);
												this.NSBoxBoolean_Multiple.current?.setValueBool(row.multiple);
												this.NSBoxTexArea_Description.current?.setValue(row.description ?? "");
											});
										});
									}
								}
							}
						}
					}
				}}
			>
				<NSColumn>
					<NSRow>
						<NSBoxEntity
							ref={this.NSBoxEntity_Product}
							title="Product"
							required
							multiple={false}
							list={async (filters, page, size, sorts) =>
							{
								let ans = await this.props.servers.product.product.List(filters, page, size, sorts);
								ans.rows = ans.rows.filter(r =>
								{
									let entities = FieldEntities.getFieldEntities(r.id);
									let names = Object.keys(entities);
									if (names)
										if (names.length > 0)
											return true;
									return false;
								});
								ans.rows = ans.rows.sort((a, b) => a.name.localeCompare(b.name));
								return ans;
							}}
							table_name="product"
							getValue={item => item.id}
							getTitle={item => item.name}
							getSort={() => null}
							onChanged={() =>
							{
								let product = this.NSBoxEntity_Product.current?.getValueOne();
								let entities: { [name: string]: NSBaseMetaTable<NSBaseServer, {}>; } = {};
								if (product)
									entities = FieldEntities.getFieldEntities(product);
								this.setState({ entities }, () =>
								{
									this.NSBoxCombo_Entity.current?.setValue(null, () =>
									{
										this.NSBoxCombo_Entity.current?.reload();
									});
								});
							}}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-product" }}
						/>
						<NSBoxCombo
							ref={this.NSBoxCombo_Entity}
							title="Entity"
							required
							multiple={false}
							getOptions={() => Object.keys(this.state.entities).map(key => ({
								value: `${key}`,
								title: `${this.state.entities[key].text}`
							}))}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-entity" }}
						/>
						<NSBoxString
							ref={this.NSBoxString_Tab}
							title="Tab"
							required
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-tab" }}
						/>
					</NSRow>
					<NSRow style={{ justifyContent: "flex-start" }}>
						<NSBoxString
							ref={this.NSBoxString_Name}
							title="Name"
							required
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-name" }}
						/>
						<NSBoxCombo
							ref={this.NSBoxCombo_EntityType}
							title="Entity Type"
							required
							multiple={false}
							getOptions={() => Object.values(SlotEntityType).map(v => ({ value: v, title: v }))}
							onChanged={() =>
							{
								let entity_type = this.NSBoxCombo_EntityType.current?.getValueOne() as SlotEntityType | null;
								this.setState({ entity_type }, () =>
								{
									this.NSBoxEntity_Field.current?.setValue(null);
									this.NSBoxEntity_CategoryScope.current?.setValue(null);
								});
							}}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-entity-type" }}
						/>
						{this.state.entity_type === SlotEntityType.Field && (
							<NSBoxEntity
								ref={this.NSBoxEntity_Field}
								title="Field"
								required
								multiple={false}
								list={async (filters, page, size, sorts) =>
								{
									return await this.props.servers.field.field.List(filters, page, size, sorts, null, {});
								}}
								table_name="field"
								getValue={item => item.id}
								getTitle={item => item.name}
								getSort={() => null}
								info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-entity-id" }}
							/>
						)}
						{this.state.entity_type === SlotEntityType.CategoryScope && (
							<NSBoxEntity
								ref={this.NSBoxEntity_CategoryScope}
								title="Category Scope"
								required
								multiple={false}
								list={async (filters, page, size, sorts) =>
								{
									return await this.props.servers.field.category_scope.List(filters, page, size, sorts, null, {});
								}}
								table_name="category_scope"
								getValue={item => item.id}
								getTitle={item => item.name}
								getSort={() => null}
								info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-entity-id" }}
							/>
						)}
					</NSRow>
					<NSRow>
						<NSBoxCombo
							ref={this.NSBoxCombo_PositionSide}
							title="Position Side"
							required
							multiple={false}
							getOptions={() => Object.values(SlotPositionSide).map(v => ({ value: v, title: v }))}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-position-side" }}
						/>
						<NSBoxString
							ref={this.NSBoxString_PositionTarget}
							title="Position Target"
							required
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-position-target" }}
						/>
						<NSBoxCombo
							ref={this.NSBoxCombo_Size}
							title="Size"
							required
							multiple={false}
							getOptions={() => Object.values(SlotSize).map(v => ({ value: v, title: v }))}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-size" }}
						/>
					</NSRow>
					<NSRow style={{ justifyContent: "flex-start" }}>
						<NSBoxBoolean
							ref={this.NSBoxBoolean_Required}
							title="Required"
							required={true}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-required" }}
						/>
						<NSBoxBoolean
							ref={this.NSBoxBoolean_Multiple}
							title="Multiple"
							required={true}
							info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-multiple" }}
						/>
					</NSRow>
					<NSBoxTextArea
						ref={this.NSBoxTexArea_Description}
						title="Description"
						required={false}
						style={{ width: NSBox.width.triple }}
						info={{ link: "https://namirasoft.com/field/console-guide/slot/#field-description" }}
					/>
				</NSColumn>
			</FieldLayout>
		);
	}
}